import { createAsyncThunk } from '@/redux/createAsyncThunk';
import { getAuthToken, getZipCodeGeoCoordinates } from '@/redux/modules/account/user/user.selectors';
import { getIsBot } from '@/redux/modules/analytics';
import { getDeployment } from '@/redux/modules/config';
import { getUserExcludedFacets } from '@/redux/modules/search/exclusions/searchExclusions.selectors';
import { clearPromotedSearchResults, searchPromoted } from '@/redux/modules/searchPromoted';
import { getSellerName } from '@/redux/modules/seller';
import { GlobalState } from '@/redux/store';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { SearchPageName } from '@/types/search/searchQuery';
import { isClientSide } from '@/utils/amplitude/analytics/core';
import {
    PriceResultsMatch,
    QueryIntent,
    QueryIntentFromApi,
    SearchContext,
} from '@/utils/amplitude/analytics/core.types';
import {
    trackSearchCompletedAnalytics,
    TrackSearchCompletedSearchType,
} from '@/utils/amplitude/analytics/search/trackSearchCompleted.analytics';
import { trackSearchRefinedAnalytics } from '@/utils/amplitude/analytics/search/trackSearchRefined.analytics';
import { createAmplitudeSearchQueryParams } from '@/utils/amplitude/analytics/utils/createAmplitudeSearchQueryParams';
import { getFullUrl } from '@/utils/amplitude/analytics/utils/getFullUrl';
import { AmplitudePageName } from '@/utils/getPageNameFromPath';
import { parseQueryParams } from '@/utils/queryParams';

import { getClientIpAddress } from '../browser';
import { getSearchPageSize } from '../pagination';
import { fetchSearchResults } from './search.api';
import { getAugmentedSearchQuery, getSearchCompletedTrigger } from './search.helpers';
import {
    getCurrentSearch,
    getSearchedItemIds,
    getSearchSellerItemsSearch,
    getSearchSellerItemsSearchSellerId,
    getSearchSubmittedFromPage,
    getSearchTotalFound,
    getSearchTotalSoldItems,
    getSearchUiState,
    getUseGeoLocation,
} from './search.selectors';
import {
    LOAD_SEARCH_RESULTS_SUCCESS,
    LOAD_SEARCH_RESULTS_SUCCESS_ACTION,
    QueryModifications,
    SAVE_LAST_SEARCH_FETCH_OPTIONS,
    SearchResultPayload,
    SubmitSearchPayload,
} from './search.types';

export const submitSearch = createAsyncThunk<SearchResultPayload | string, SubmitSearchPayload>(
    'la/domain/search/submitSearch',
    async (
        {
            learningToRankModelVersion,
            pageName,
            popularityScoreReRankingVersion,
            rankingVersionVariant,
            routerState = {},
            searchQuery,
            skipAggs,
            useATGSearch,
            useScoutCategories,
        },
        { dispatch, getState }
    ) => {
        const state = getState();
        const deployment = getDeployment(state);
        const authToken = getAuthToken(state);
        const ipAddress = getClientIpAddress(state);
        const isBot = getIsBot(state);
        const currentSearch = getCurrentSearch(state);
        const submittedFromPage = getSearchSubmittedFromPage(state);
        const { isZeroResults } = getSearchUiState(state);

        // Annotate facets which have user-defined search exclusions
        const searchExclusions = getUserExcludedFacets(state);
        const augmentedSearchQuery = getAugmentedSearchQuery(searchQuery, searchExclusions);

        // Set seoSearch to true if the page is an SEO search or a collections page
        const searchQueryWithSeo = {
            ...augmentedSearchQuery,
            seoSearch: pageName === SearchPageName.SEO_SEARCH || pageName === SearchPageName.COLLECTIONS,
        };

        const searchForCacheCheck = JSON.stringify(searchQueryWithSeo);
        if (searchForCacheCheck === currentSearch && !isZeroResults) {
            trackSearchCompletedOrRefined({
                deployment,
                lotIds: getSearchedItemIds(state),
                queryModifications: getSearchUiState(state).queryModifications,
                routerState,
                searchTerm: searchQuery.searchTerm,
                state,
                submittedFromPage,
                totalFound: getSearchTotalFound(state),
                totalSoldItems: getSearchTotalSoldItems(state),
            });
            return {};
        }

        const useGeoLocation = getUseGeoLocation(state);
        const geoCoordinates = getZipCodeGeoCoordinates(state);

        // searchQueryWithSeo contains search query + seoSearch flag to request SEO metadata from API if on SEO search page or collections page
        const options = {
            authToken,
            deployment,
            geoCoordinates,
            ipAddress,
            isBot,
            learningToRankModelVersion,
            popularityScoreReRankingVersion,
            rankingVersionVariant,
            searchQuery: searchQueryWithSeo,
            skipAggs,
            useATGSearch,
            useGeoLocation,
        };

        // Save options so we can re-run without skipAggs later for on-demand facets
        dispatch({
            payload: options,
            type: SAVE_LAST_SEARCH_FETCH_OPTIONS,
        });

        // main search api dispatch
        const promises: Promise<any>[] = [fetchSearchResults(options)];
        // add search for promoted items if making a catalog search AND sort is Most Relevant
        if (pageName !== 'catalog' && searchQuery.sort === SearchSortAndDirection.MostRelevant) {
            promises.push(
                dispatch(
                    searchPromoted({
                        pageName,
                        rankingVersionVariant,
                        searchQuery,
                        useATGSearch,
                        useScoutCategories,
                    })
                )
            );
        } else if (pageName !== 'catalog' && searchQuery.sort !== SearchSortAndDirection.MostRelevant) {
            // Clear promoted search results when sort is not Most Relevant
            dispatch(clearPromotedSearchResults());
        }

        const [results] = await Promise.all(promises);

        results.payload.currentQueryKey = searchForCacheCheck;

        // TODO: once all the reducers that subscribe to LOAD_SEARCH_RESULTS_SUCCESS are updated to toolkit, this dispatch should be removed
        const successAction: LOAD_SEARCH_RESULTS_SUCCESS_ACTION = {
            error: false,
            meta: { actionTime: Date.now() },
            payload: skipAggs
                ? { ...(results.payload as SearchResultPayload), soldItemFacets: null }
                : (results.payload as SearchResultPayload),
            type: LOAD_SEARCH_RESULTS_SUCCESS,
        };

        dispatch(successAction);

        if (!isZeroResults) {
            trackSearchCompletedOrRefined({
                deployment,
                lotIds: results.payload.itemIDs,
                queryModifications: results.payload.queryModifications,
                routerState,
                searchTerm: searchQuery.searchTerm,
                state,
                submittedFromPage,
                totalFound: results.payload.totalFound,
                totalSoldItems: results.payload.totalSold,
            });
        }

        // When skipAggs, null out soldItemFacets so the on-demand aggregation fetch knows
        // full aggregations haven't been loaded yet (matches the legacy action payload above)
        return skipAggs ? { ...(results.payload as SearchResultPayload), soldItemFacets: null } : results.payload;
    }
);

type TrackSearchCompletedParams = {
    deployment: ReturnType<typeof getDeployment>;
    lotIds: number[];
    queryModifications?: QueryModifications;
    routerState: SubmitSearchPayload['routerState'];
    searchTerm: string;
    state: GlobalState;
    submittedFromPage: AmplitudePageName;
    totalFound: number;
    totalSoldItems: number;
};

const trackSearchCompletedOrRefined = ({
    deployment,
    lotIds,
    queryModifications,
    routerState,
    searchTerm,
    state,
    submittedFromPage,
    totalFound,
    totalSoldItems,
}: TrackSearchCompletedParams) => {
    if (isClientSide()) {
        const pageSize = getSearchPageSize(state);
        const sanitizedAnalyticsQueryParams = createAmplitudeSearchQueryParams(
            window?.location?.search ?? '',
            pageSize
        );

        const analyticsLotIds = lotIds.join(',');

        // Facet applied (and not a related-search suggestion click). Tracking Search Refined
        if (routerState.filterAction && !routerState.suggestionClicked) {
            const trackSearchRefinedPayload = {
                lotIds: analyticsLotIds,
                normalizedSearchQuery: searchTerm?.toLowerCase() ?? '',
                numberOfSearchResults: totalFound,
                pageName: submittedFromPage,
                searchQuery: searchTerm,
                url: getFullUrl(deployment, window.location.pathname),
                ...sanitizedAnalyticsQueryParams,
            };
            trackSearchRefinedAnalytics(trackSearchRefinedPayload);
        } else {
            // No facet applied. Tracking Search Completed
            const isSellerItemsSearch = getSearchSellerItemsSearch(state);
            const sellerId = getSearchSellerItemsSearchSellerId(state);
            const sellerName = getSellerName(state, sellerId);

            const fromSellerPage = isSellerItemsSearch && !submittedFromPage && !searchTerm;

            const searchInput = document.getElementById('search-input') as HTMLInputElement;
            const submittedFromSearchBar = searchInput?.value === searchTerm;

            // This gets added in useSearchProps.ts
            const searchSuggestionUsed = Boolean(routerState?.suggestionClicked);

            // Determine search context based on URL parameters
            const queryParams = parseQueryParams(window?.location?.search ?? '');
            const { pathname } = window.location;
            const isArchivedSearch = queryParams.status === 'archive' || pathname.includes('/price-guide');
            const searchContext = isArchivedSearch ? SearchContext.PriceResults : SearchContext.UpcomingItems;

            let queryIntent: QueryIntent | undefined = QueryIntent.None;
            if (queryModifications?.queryRewrite?.applied) {
                const queryIntentApiValue = queryModifications?.queryRewrite?.queryIntent;
                queryIntent = queryIntentApiValue
                    ? (QueryIntentFromApi[queryIntentApiValue] ?? QueryIntent.None)
                    : QueryIntent.None;
            }

            let modifiedQuery: string | undefined;
            if (queryIntent !== QueryIntent.None) {
                const modifiedQueryValue = queryModifications?.queryRewrite?.modifiedQuery;
                modifiedQuery = modifiedQueryValue === '' ? '(empty)' : modifiedQueryValue;
            }

            const trackSearchCompletedPayload = {
                lotIds: analyticsLotIds,
                modifiedQuery,
                normalizedSearchQuery: (fromSellerPage ? sellerName : searchTerm)?.toLowerCase() ?? '',
                numberOfSearchResults: totalFound,
                page: sanitizedAnalyticsQueryParams.page,
                pageName: fromSellerPage ? AmplitudePageName.Auctioneer : submittedFromPage,
                pageSize,
                priceResultsMatch: totalSoldItems > 0 ? PriceResultsMatch.Yes : PriceResultsMatch.No,
                queryIntent,
                searchContext,
                searchQuery: fromSellerPage ? sellerName : searchTerm,
                searchSuggestionUsed,
                searchType: fromSellerPage
                    ? TrackSearchCompletedSearchType.Auctioneer
                    : TrackSearchCompletedSearchType.Keyword,
                trigger: getSearchCompletedTrigger(
                    sanitizedAnalyticsQueryParams.trigger,
                    searchSuggestionUsed,
                    submittedFromSearchBar
                ),
                url: getFullUrl(deployment, window.location.pathname),
            };

            trackSearchCompletedAnalytics(trackSearchCompletedPayload);
        }
    }
};

export const updateSearchSubmittedFromPage = createAsyncThunk<AmplitudePageName, AmplitudePageName>(
    'la/domain/search/updateSearchSubmittedFromPage',
    (pageName) => pageName
);

export const updateSearchSellerItemsSearch = createAsyncThunk<boolean, boolean>(
    'la/domain/search/updateSearchSellerItemsSearch',
    (sellerItemsSearch) => sellerItemsSearch
);

export const updateSearchSellerItemsSearchSellerId = createAsyncThunk<number, number>(
    'la/domain/search/updateSearchSellerItemsSearchSellerId',
    (sellerItemsSearch) => sellerItemsSearch
);

export const fetchMoreSearchResults = createAsyncThunk<SearchResultPayload | string, SubmitSearchPayload>(
    'la/domain/search/fetchMoreSearchResults',
    async ({ pageName, searchQuery, skipAggs, useATGSearch, useScoutCategories }, { dispatch, getState }) => {
        const state = getState();
        const deployment = getDeployment(state);
        const authToken = getAuthToken(state);
        const ipAddress = getClientIpAddress(state);
        const isBot = getIsBot(state);

        // Annotate facets which have user-defined search exclusions
        const searchExclusions = getUserExcludedFacets(state);
        const augmentedSearchQuery = getAugmentedSearchQuery(
            { ...(isBot ? { isBot } : {}), ...searchQuery },
            searchExclusions
        );

        const useGeoLocation = getUseGeoLocation(state);
        const geoCoordinates = getZipCodeGeoCoordinates(state);

        const promises: Promise<any>[] = [
            fetchSearchResults({
                authToken,
                deployment,
                geoCoordinates,
                ipAddress,
                searchQuery: augmentedSearchQuery,
                skipAggs,
                useATGSearch,
                useGeoLocation,
            }),
        ];

        if (pageName !== 'catalog' && searchQuery.sort === SearchSortAndDirection.MostRelevant) {
            promises.push(dispatch(searchPromoted({ pageName, searchQuery, useATGSearch, useScoutCategories })));
        } else if (pageName !== 'catalog' && searchQuery.sort !== SearchSortAndDirection.MostRelevant) {
            // Clear promoted search results when sort is not Most Relevant
            dispatch(clearPromotedSearchResults());
        }

        const [results] = await Promise.all(promises);
        return results.payload;
    }
);

/**
 * Re-runs the last search WITHOUT skipAggs to fetch search-scoped facets on demand.
 * Only updates facets in state — does NOT update items, loading, or other search state.
 * Skips the fetch if facets are already loaded or currently loading.
 */
export const fetchSearchAggregationsOnDemand = createAsyncThunk<SearchResultPayload | null, void>(
    'la/domain/search/fetchSearchAggregationsOnDemand',
    async (_, { dispatch, getState }) => {
        const state = getState();
        const lastOptions = state.search.lastSearchFetchOptions;
        if (!lastOptions) {
            return null;
        }

        const results = await fetchSearchResults({
            ...lastOptions,
            aggsOnly: true,
            skipAggs: undefined, // explicitly DO NOT skip aggregations
        });

        if (results.error) {
            throw new Error('Failed to fetch search aggregations');
        }

        // Dispatch legacy action so searchFacets reducer picks up soldItemFacets
        const successAction: LOAD_SEARCH_RESULTS_SUCCESS_ACTION = {
            error: false,
            meta: { actionTime: Date.now() },
            payload: results.payload as SearchResultPayload,
            type: LOAD_SEARCH_RESULTS_SUCCESS,
        };
        dispatch(successAction);

        return results.payload as SearchResultPayload;
    },
    {
        condition: (_, { getState }) => {
            const { aggregationsOnDemandStatus, lastSearchFetchOptions } = getState().search;
            // Skip if already loading/loaded, or if the last search didn't skip aggs
            // (non-skipAggs search already returned full aggregations — no on-demand fetch needed).
            // Note: soldItemFacets !== null cannot be used here because live searches always return
            // soldItemFacets: null, so it would never block for live searches.
            if (
                aggregationsOnDemandStatus === 'loading' ||
                aggregationsOnDemandStatus === 'loaded' ||
                !lastSearchFetchOptions?.skipAggs
            ) {
                return false;
            }
        },
    }
);

export const setUseGeoLocation = createAsyncThunk<boolean, boolean>(
    'la/domain/search/setUseGeoLocation',
    async (useGeoLocation) => useGeoLocation
);

export const setIsZeroResults = createAsyncThunk<boolean, boolean>(
    'la/domain/search/setIsZeroResults',
    async (isZeroResults) => isZeroResults
);
